import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, Youtube } from "lucide-react";

// subtle grain (keep it, but make it softer)
const NOISE_SVG =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='220' height='220'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.75' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='220' height='220' filter='url(%23n)' opacity='.22'/%3E%3C/svg%3E";

export default function Footer() {
  const columns = [
    {
      title: "Company",
      links: [
        { label: "About Us", to: "/about" },
        { label: "Blog", href: "https://journal.thenutrafox.com/journal", external: true },
        { label: "Careers", to: "/careers" },
        { label: "Partnerships", to: "/partnerships" },
      ],
    },
    {
      title: "Resources",
      links: [
        { label: "Customer Support", to: "/support" },
        { label: "Service", to: "/service" },
        { label: "Corporate Sales", to: "/corporate-sales" },
        { label: "Financing", to: "/financing" },
      ],
    },
    {
      title: "Account",
      links: [
        { label: "Log-in", to: "/login" },
        // you currently use /my-orders in App.js (not /orders)
        { label: "Orders", to: "/my-orders" },
      ],
    },
  ];

  return (
    <footer className="relative overflow-hidden text-[#FAFAF5]">
      {/* NutraFox gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0f2f1c] via-[#1E5631] to-[#0b1f14]" />

      {/* soft gold highlight */}
      <div className="pointer-events-none absolute -top-24 left-1/2 h-72 w-[900px] -translate-x-1/2 rounded-full bg-[#D4AF57]/10 blur-3xl" />

      {/* subtle grain */}
      <div
        className="pointer-events-none absolute inset-0 opacity-25 mix-blend-overlay"
        style={{
          backgroundImage: `url("${NOISE_SVG}")`,
          backgroundRepeat: "repeat",
        }}
      />

      {/* top border line (premium separator) */}
      <div className="relative h-px w-full bg-gradient-to-r from-transparent via-[#D4AF57]/45 to-transparent" />

      {/* Top content */}
      <div className="relative mx-auto max-w-7xl px-6 pt-14">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
          {/* Left big list */}
          <div className="md:col-span-3">
            <div className="space-y-2 text-3xl font-light tracking-wide text-[#FAFAF5]/90">
              <div className="hover:text-[#D4AF57] transition">Raw</div>
              <div className="hover:text-[#D4AF57] transition">Roasted</div>
              <div className="hover:text-[#D4AF57] transition">Flavoured</div>
              <div className="hover:text-[#D4AF57] transition">Gift Packs</div>
            </div>
          </div>

          {/* Columns */}
          <div className="md:col-span-9">
            <div className="grid grid-cols-2 gap-10 sm:grid-cols-3">
              {columns.map((col) => (
                <div key={col.title}>
                  <div className="mb-4 text-lg font-semibold tracking-wide text-[#FAFAF5]/95">
                    {col.title}
                  </div>
                  <ul className="space-y-3 text-sm tracking-[0.18em] text-[#FAFAF5]/70">
                    {col.links.map((l) => (
                      <li key={l.label}>
                        {l.external ? (
                            <a
                              href={l.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block hover:text-[#D4AF57] transition"
                            >
                              {l.label.toUpperCase()}
                            </a>
                          ) : (
                            <Link
                              to={l.to}
                              className="inline-block hover:text-[#D4AF57] transition"
                            >
                              {l.label.toUpperCase()}
                            </Link>
                          )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Social + legal row */}
            <div className="mt-10 flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-4 text-[#FAFAF5]/85">
                <a
                  href="https://www.instagram.com/thenutrafox/"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-[#D4AF57] transition"
                  aria-label="Instagram"
                >
                  <Instagram size={18} />
                </a>
                <a
                  href="https://www.linkedin.com/company/thenutrafox/"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-[#D4AF57] transition"
                  aria-label="LinkedIn"
                >
                  <Linkedin size={18} />
                </a>
                <a
                  href="https://www.youtube.com/@TheNutraFox"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-[#D4AF57] transition"
                  aria-label="YouTube"
                >
                  <Youtube size={18} />
                </a>
              </div>

              <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs tracking-[0.22em] text-[#FAFAF5]/70">
                <Link to="/media-inquiries" className="hover:text-[#D4AF57] transition">
                  MEDIA INQUIRIES
                </Link>
                <Link to="/terms" className="hover:text-[#D4AF57] transition">
                  TERMS
                </Link>
                <Link to="/privacy-policy" className="hover:text-[#D4AF57] transition">
                  PRIVACY
                </Link>
                <Link to="/supplier-terms" className="hover:text-[#D4AF57] transition">
                  SUPPLIER TERMS
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Giant brand word section */}
        <div className="relative mx-auto max-w-7xl px-6 pb-12 pt-12">
        {/* On mobile: allow horizontal scroll so it never crops */}
        <div
            className="select-none whitespace-nowrap font-extrabold tracking-tight
                    overflow-x-auto sm:overflow-visible -mx-6 px-6"
            style={{
            color: "rgba(212,175,87,0.92)",
            textShadow: "0 14px 50px rgba(0,0,0,0.35)",
            fontSize: "clamp(46px, 11vw, 190px)",
            lineHeight: 0.9,
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none", // Firefox hide scrollbar
            }}
        >
            {/* Hide scrollbar on Chrome/Safari */}
            <style>{`
            .nf-brand-scroll::-webkit-scrollbar { display: none; }
            `}</style>

            <div className="nf-brand-scroll">
            THE&nbsp;NUTRAFOX
            <span
                className="inline-block ml-2 align-super"
                style={{
                fontSize: "clamp(13px, 2.3vw, 26px)",
                opacity: 0.95,
                transform: "translateY(-0.25em)",
                whiteSpace: "nowrap",
                }}
            >
                ™
            </span>
            </div>
        </div>

        <div className="mt-6 text-[11px] tracking-[0.25em] text-[#FAFAF5]/55">
            © {new Date().getFullYear()} THE NUTRAFOX. ALL RIGHTS RESERVED.
        </div>
        </div>
    </footer>
  );
}
